import React, { useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import {
  useDeletePromptVersionMutation,
  useGetPromptVersionsQuery,
} from "apis/promptVersion";
import { formatDateTime } from "utils/helperFunctions";

function PromptVersions({ promptId, versionsRoute }) {
  const [confirmingId, setConfirmingId] = useState(null);
  const { data: versions, isPending } = useGetPromptVersionsQuery(
    versionsRoute,
    promptId
  );
  const {
    mutate: deleteVersion,
    isPending: isDeleting,
    variables: deletingVars,
  } = useDeletePromptVersionMutation(versionsRoute);

  if (isPending) {
    return <p className="text-sm text-darkgray">Loading versions…</p>;
  }

  if (!versions.length) {
    return <p className="text-sm text-darkgray">No previous versions.</p>;
  }

  return (
    <ul className="space-y-3">
      {versions.map((v) => {
        const isConfirming = confirmingId === v._id;
        const isThisDeleting =
          isDeleting && deletingVars?.versionId === v._id;
        return (
          <li key={v._id} className="border rounded-md p-3 bg-white shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs text-darkgray">
                Replaced: {formatDateTime(v.createdAt)}
                {v.updatedBy?.name && ` by ${v.updatedBy.name}`}
              </p>
              {isConfirming ? (
                <div className="flex items-center gap-2 text-xs shrink-0">
                  <span className="text-darkgray">Delete?</span>
                  <button
                    type="button"
                    className="text-red-600 hover:text-red-700 font-medium"
                    disabled={isThisDeleting}
                    onClick={() =>
                      deleteVersion(
                        { promptId, versionId: v._id },
                        { onSettled: () => setConfirmingId(null) }
                      )
                    }
                  >
                    {isThisDeleting ? "Deleting…" : "Yes"}
                  </button>
                  <button
                    type="button"
                    className="text-darkgray hover:text-black"
                    disabled={isThisDeleting}
                    onClick={() => setConfirmingId(null)}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="delete-icon shrink-0"
                  title="Delete version"
                  onClick={() => setConfirmingId(v._id)}
                >
                  <FiTrash2 fontSize={16} />
                </button>
              )}
            </div>

            <pre className="whitespace-pre-wrap mt-1">{v.description}</pre>
          </li>
        );
      })}
    </ul>
  );
}

export default function PromptView({
  promptId,
  versionsRoute,
  name,
  description,
  organizationName,
  styleName,
}) {
  return (
    <div className="border-t border-gray-200">
      <dl>
        {/* Name */}
        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
          <dt className="text-sm font-medium text-darkgray">Name</dt>
          <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">{name}</dd>
        </div>

        {(organizationName || styleName) && (
          <>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-darkgray">Organization</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                {organizationName || "—"}
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-darkgray">Style</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                {styleName || "—"}
              </dd>
            </div>
          </>
        )}

        {/* Current Description */}
        <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
          <dt className="text-sm font-medium text-darkgray">Description</dt>
          <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
            <pre className="whitespace-pre-wrap">{description}</pre>
          </dd>
        </div>

        {/* Version History */}
        {promptId && versionsRoute && (
          <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-darkgray">
              Version History
            </dt>
            <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
              <PromptVersions
                promptId={promptId}
                versionsRoute={versionsRoute}
              />
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}
